'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';

interface MessageInputProps {
  onSend: (message: string) => void;
  disabled?: boolean;
  error?: string | null;
  isWaitingForResponse?: boolean;
  onActivate?: () => void;
  placeholder?: string;
  placeholderWaiting?: string;
  placeholderDisabled?: string;
  buttonText?: string;
  showButtonAsArrow?: boolean;
}

const MessageInput: React.FC<MessageInputProps> = ({
  onSend,
  disabled,
  error,
  isWaitingForResponse,
  onActivate,
  placeholder = 'Opisz krótko swoją sprawę',
  placeholderWaiting = 'Analizuję Twoją sprawę...',
  placeholderDisabled = 'Łączenie...',
  buttonText,
  showButtonAsArrow = true,
}) => {
  const [message, setMessage] = useState('');
  const [visibleError, setVisibleError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const errorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const errorDisplayTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (error) {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
      if (errorDisplayTimeoutRef.current) {
        clearTimeout(errorDisplayTimeoutRef.current);
      }

      if (
        error.includes('Not connected') ||
        error.includes('Connection timeout')
      ) {
        errorDisplayTimeoutRef.current = setTimeout(() => {
          setVisibleError(error);
        }, 3000);
      } else {
        setVisibleError(error);
      }

      if (error.includes('connection') || error.includes('connect')) {
        errorTimeoutRef.current = setTimeout(() => {
          setVisibleError(null);
        }, 30000);
      }
    } else {
      setVisibleError(null);
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
      if (errorDisplayTimeoutRef.current) {
        clearTimeout(errorDisplayTimeoutRef.current);
      }
    }

    return () => {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
      if (errorDisplayTimeoutRef.current) {
        clearTimeout(errorDisplayTimeoutRef.current);
      }
    };
  }, [error]);

  useEffect(() => {
    const focusTextarea = () => {
      // Only focus on desktop (min-width: 768px)
      if (
        textareaRef.current &&
        !disabled &&
        window.matchMedia('(min-width: 768px)').matches
      ) {
        textareaRef.current.focus();
      }
    };
    setTimeout(focusTextarea, 100);
  }, [disabled]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [message]);

  const handleSubmit = useCallback(
    (e: React.FormEvent | React.MouseEvent) => {
      e.preventDefault();
      if (!message.trim() || disabled) return;
      onSend(message.trim());
      setMessage('');
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    },
    [message, disabled, onSend]
  );

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit(e);
      }
    },
    [handleSubmit]
  );

  const getPlaceholderText = () => {
    if (isWaitingForResponse) return placeholderWaiting;
    if (disabled) return placeholderDisabled;
    return placeholder;
  };

  return (
    <form
      className="relative max-w-2xl mx-auto w-full"
      onSubmit={handleSubmit}
      aria-label="Formularz wiadomosci"
    >
      {visibleError && (
        <div
          className="absolute bottom-full mb-2 left-0 right-0 px-4 py-2 bg-red-500 bg-opacity-10 border border-red-500 rounded-card"
          role="alert"
          aria-live="polite"
        >
          <p className="text-red-400 text-sm">{visibleError}</p>
        </div>
      )}
      <div
        className="flex items-stretch rounded-card overflow-hidden ring-2 ring-[#F5CB5C] bg-white/90 backdrop-blur"
        style={{
          boxShadow: '0 4px 20px rgba(245, 203, 92, 0.25)',
        }}
      >
        <textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyPress}
          onClick={onActivate}
          placeholder={getPlaceholderText()}
          readOnly={disabled}
          rows={1}
          aria-label="Wpisz wiadomosc"
          aria-disabled={disabled}
          className={`flex-1 min-w-0 border-0 py-3 pl-12 pr-4 bg-transparent text-[#242424]
            placeholder:text-[#242424] focus:outline-none focus:ring-0
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
            resize-none max-h-48 overflow-hidden`}
        />
        <button
          type="submit"
          disabled={!message.trim() || disabled}
          aria-label={showButtonAsArrow || !buttonText ? 'Wyslij wiadomosc' : buttonText}
          className={`flex-shrink-0 bg-[#F5CB5C] flex items-center justify-center transition-all rounded-[24px]
            ${showButtonAsArrow || !buttonText ? 'w-20' : 'px-5'}
            ${!message.trim() || disabled ? 'cursor-not-allowed opacity-60' : 'hover:opacity-90'}`}
        >
          {showButtonAsArrow || !buttonText ? (
            <img src="/assets/send-arrow.svg" alt="" aria-hidden="true" className="w-6 h-6" />
          ) : (
            <span className="flex items-center gap-2 text-lawmingo-charcoal font-medium text-sm whitespace-nowrap">
              {buttonText}
              <img
                src="/assets/send-arrow.svg"
                alt=""
                aria-hidden="true"
                className="w-5 h-5"
              />
            </span>
          )}
        </button>
      </div>
    </form>
  );
};

export default React.memo(MessageInput);
